import ko from "knockout";
import _ from "lodash";

const combine = function (sourceData, destinationModel, strategy, options) {
    _.each(sourceData, function (value, key) {
        const isIdentifier = key === options.identifier;

        // If this is not the identifier field, combine it into the destination
        if (!isIdentifier) {
            observableCombine(key, value, destinationModel, strategy, options);

            // If it is the identifier and identifier is not set
        } else if (destinationModel[key] === undefined) {
            destinationModel[key] = value;
        }
    });
};

const observableCombine = function (
    sourceKey,
    sourceValue,
    destination,
    strategy,
    options
) {
    const destinationExists = destination[sourceKey] !== undefined;
    const ModelConstructor = options.modelProperties[sourceKey];

    // If the field being updated is an observable model, use the strategy defined for observable models.
    if (ModelConstructor) {
        if (destinationExists) {
            strategy.updateObservableModel(
                sourceKey,
                sourceValue,
                destination,
                strategy,
                ModelConstructor
            );
        } else {
            strategy.createObservableModel(
                sourceKey,
                sourceValue,
                destination,
                strategy,
                ModelConstructor
            );
        }

        // If we are updating an object, make sure it exists and is observable, then recurse into it.
    } else if (_.isPlainObject(sourceValue)) {
        if (!destinationExists) {
            destination[sourceKey] = ko.observable({});
        } else if (!ko.unwrap(destination[sourceKey])) {
            destination[sourceKey]({});
        }
        _.each(sourceValue, function (value, key) {
            observableCombine(
                key,
                value,
                destination[sourceKey](),
                strategy,
                options
            );
        });

        // If we are updating an array or primitive value, use the regular strategies.
    } else {
        if (destinationExists) {
            strategy.update(sourceKey, sourceValue, destination);
        } else {
            strategy.create(sourceKey, sourceValue, destination);
        }
    }
};

const updateObservableModel = function (
    sourceKey,
    sourceValue,
    destination,
    strategy,
    ModelConstructor
) {
    const options = ModelConstructor.options,
        targetModel = destination[sourceKey],
        unWrappedTargetModel = ko.unwrap(destination[sourceKey]);

    // If the new model has a different id than the old, replace the old model
    if (
        unWrappedTargetModel[options.identifier] !=
        sourceValue[options.identifier]
    ) {
        strategy.update(
            sourceKey,
            new ModelConstructor(sourceValue),
            destination
        );

        // If the values are arrays, update the existing one in place to match the new one
    } else if (_.isArray(unWrappedTargetModel)) {
        strategy.combineArray(
            targetModel,
            sourceValue,
            strategy,
            ModelConstructor
        );

        // Otherwise, just combine the new data into the existing model
    } else {
        combine(sourceValue, unWrappedTargetModel, strategy, options);
    }
};

const createObservableModel = function (
    sourceKey,
    sourceValue,
    destination,
    strategy,
    ModelConstructor
) {
    let modelArray;

    // If we are creating an array, map the source array to observable models
    if (_.isArray(sourceValue)) {
        modelArray = _.map(sourceValue, function (data) {
            return new ModelConstructor(data);
        });
        destination[sourceKey] = ko.observableArray(modelArray);

        // Otherwise just create a new observable instance of the model class
    } else {
        destination[sourceKey] = ko.observable(
            new ModelConstructor(sourceValue)
        );
    }
};

const updateObservable = function (sourceKey, sourceValue, destination) {
    destination[sourceKey](sourceValue);
};

const fillUpdateObservable = function (sourceKey, sourceValue, destination) {
    if (ko.isObservable(destination[sourceKey])) {
        // If the observable value is undefined, replace it with the new value
        if (_.isUndefined(destination[sourceKey]())) {
            destination[sourceKey](sourceValue);
        }
    } else {
        if (_.isArray(sourceValue)) {
            destination[sourceKey] = ko.observableArray(sourceValue);
        } else {
            destination[sourceKey] = ko.observable(sourceValue);
        }
    }
};

const createObservable = function (sourceKey, sourceValue, destination) {
    if (_.isArray(sourceValue)) {
        destination[sourceKey] = ko.observableArray(sourceValue);
    } else {
        destination[sourceKey] = ko.observable(sourceValue);
    }
};

/**
 * Fills the entries of an array of {@link ObservableModel}s with raw data from another array.
 * {@link ObservableModel}s in the existing array are matched with raw entities in the incoming
 * array by identifier, and are combined when they match.
 * @param {array} modelArray of {@link ObservableModel}s to update
 * @param {array} dataArray of raw data representing the updated state
 * @param {function} ModelClass constructor class for the data in the array
 */
const fillModelArray = function (modelArray, dataArray, strategy, ModelClass) {
    const identifier = ModelClass.options.identifier;
    const rawModelArray = ko.utils.unwrapObservable(modelArray);
    const modelIdx = {};
    _.forEach(rawModelArray, function (model) {
        const id = ko.utils.unwrapObservable(model[identifier]);
        modelIdx[id] = model;
    });

    _.forEach(dataArray, function (data) {
        const id = data[identifier],
            model = modelIdx[id];

        if (model) {
            combine(data, model, strategy, ModelClass.options);
        }
    });
};

/**
 * Updates the state of an array of {@link ObservableModel}s to match an array of raw data.
 * {@link ObservableModel}s in the existing array are matched with data in the new array
 * by identifier, and are updated instead of being replaced.
 * @param {array} modelArray of {@link ObservableModel}s to update
 * @param {array} dataArray of raw data representing the updated state
 * @param {function} ModelClass constructor class for the data in the array
 */
const mergeModelArray = function (modelArray, dataArray, strategy, ModelClass) {
    const identifier = ModelClass.options.identifier;
    const rawModelArray = ko.utils.unwrapObservable(modelArray);
    //Build an index for easy lookup of existing models
    const modelIdx = {};
    _.forEach(rawModelArray, function (model) {
        const id = ko.utils.unwrapObservable(model[identifier]);
        modelIdx[id] = model;
    });

    //Add to and shift models in the existing array
    //to match the incoming array.
    modelArray.valueWillMutate();
    _.forEach(dataArray, function (data, idx) {
        const id = data[identifier];
        let model = modelIdx[id];

        if (!model) {
            model = new ModelClass(data);
        } else {
            combine(data, model, strategy, ModelClass.options);
        }

        if (idx < rawModelArray.length) {
            rawModelArray.splice(idx, 1, model);
        } else {
            rawModelArray[idx] = model;
        }
    });

    //Remove any models that aren't in the new array
    if (rawModelArray.length > dataArray.length) {
        const toRemove = rawModelArray.length - dataArray.length;
        rawModelArray.splice(-toRemove, toRemove);
    }

    modelArray.valueHasMutated();
};

// Strategy for use in merging data into observable models.
const mergeStrategy = {
    update: updateObservable,
    create: createObservable,
    updateObservableModel: updateObservableModel,
    createObservableModel: createObservableModel,
    combineArray: mergeModelArray,
};

// Fill is like merge, but we never update existing values
// to put it another way, existing values take precedence
// over incoming values.
const fillStrategy = {
    update: fillUpdateObservable,
    create: createObservable,
    updateObservableModel: updateObservableModel,
    createObservableModel: createObservableModel,
    combineArray: fillModelArray,
};

// The default options for view Models
const defaultOptions = {
    identifier: "id",
    modelProperties: {},
    defaults: {},
};

/**
 * The ObservableModel class is an extensible mechanism for transforming
 * raw data representations into (potentially nested) knockout view models with
 * observable fields. It provides features for creating new instances from
 * raw data, and for merging updates into existing instances.
 * @exports util/ObservableModel
 */
const ObservableModel = function (jsonData) {
    // We clone the defaults because things like arrays will be extended by reference
    // and we don't want to have instances editing the defaults array itself.
    const data = _.extend(
        {},
        _.cloneDeep(this.constructor.options.defaults),
        jsonData
    );
    if (jsonData) {
        this.id = jsonData[this.constructor.options.identifier];
    }
    this.constructor.merge(this, data);
};

/**
 * The options that configure how models of this type are merged
 */
ObservableModel.options = _.cloneDeep(defaultOptions);

/**
 * Extends an existing {@link ObservableModel} class & updates behaviors.
 * The new class will inherit configuration options from its parent.
 * @param {object} options Configuration options for the child {@link ObservableModel}
 * the following options can be set in this object:<br/>
 * <dl>
 *  <dt>identifier</dt>
 *  <dd>{@link string}: The property that represents the unique id for this class</dd>
 *  <dt>staticProperties</dt>
 *  <dd>{@link array}({@link string}): List of properties that should not be observable</dd>
 *  <dt>modelProperties</dt>
 *  <dd>
 *    {@link object}: Mapping of property names to {@link ObservableModel} constructors that should be
 *    used to when populating those properties from raw data
 *  </dd>
 *  <dt>defaults</dt>
 *  <dd>
 *    {@link object}: Default data for new instances of the class.
 *  </dd>
 * </dl>
 */
ObservableModel.extend = function (options, initialize) {
    const SuperClass = this,
        superOpts = SuperClass.options,
        opts = _.extend({}, defaultOptions, options);

    const NewClass = function (/* jsonData */) {
        SuperClass.apply(this, arguments);
        if (initialize) {
            initialize.apply(this, arguments);
        }
    };
    NewClass.extend = SuperClass.extend;
    NewClass.options = {
        identifier: opts.identifier,
        modelProperties: _.extend(
            {},
            opts.modelProperties,
            superOpts.modelProperties
        ),
        defaults: _.extend({}, opts.defaults, superOpts.defaults),
    };
    NewClass.merge = SuperClass.merge;
    NewClass.fill = SuperClass.fill;
    NewClass.unwrap = SuperClass.unwrap;

    return NewClass;
};

/**
 * Merges raw data into an existing {@link ObservableModel}. Observable fields
 * will be updated & static fields will be overwritten. If a property
 * is defined as a subclass of {@link ObservableModel}, the provided data will be
 * merged into it.
 * @param {ObservableModel} viewModel the existing model to merge data into.
 * @param {object} data the data to merge into the model.
 */
ObservableModel.merge = function (viewModel, data) {
    const options = this.options;
    combine(data, viewModel, mergeStrategy, options);
};

ObservableModel.fill = function (viewModel, data) {
    const options = this.options;
    combine(data, viewModel, fillStrategy, options);
};

/**
 * Creates a deep clone of this object unwrapping each observable value
 * into a plain javascript object.
 * @param {ObservableModel} source The ObservableModel to unwrap.
 * @param {array} ignoredProperties An array of properties to ignore
 */
ObservableModel.unwrap = function (source, ignoredProperties) {
    const destination = {};
    const options = this.options;
    unwrapRecursive(source, destination, ignoredProperties, options);
    return destination;
};

const unwrapRecursive = function (
    source,
    destination,
    ignoredProperties,
    options,
    parentArraykey
) {
    for (const key in source) {
        if (ignoredProperties && ignoredProperties.indexOf(key) >= 0) {
            continue;
        }

        // If the object is in modelProperties, then call its native unwrap to ensure we
        // skip the ignored properties.
        let ModelConstructor;
        if (options) {
            // If we're iterating an array, key will be "0" but we really want to unwrap based
            // on the name of the array property on the parent
            ModelConstructor =
                options.modelProperties[key] ||
                options.modelProperties[parentArraykey];
        }

        const value = ko.utils.unwrapObservable(source[key]);

        if (value === null) {
            destination[key] = null;
        } else if (value instanceof Array) {
            destination[key] = [];

            unwrapRecursive(value, destination[key], undefined, options, key);
        } else if (value instanceof File) {
            destination[key] = value;
        } else if (typeof value === "object") {
            // If someone has assigned a non-observableModel object directly to a property
            // that has a ModelConstructor, then just take what they set.  Otherwise be
            // sure to call unwrap.
            if (ModelConstructor && _.isFunction(value.unwrap)) {
                destination[key] = value.unwrap();
            } else {
                destination[key] = {};
                unwrapRecursive(value, destination[key]);
            }
        } else {
            destination[key] = value;
        }
    }
};

export default ObservableModel;
